/**
 * Kundengruppen-Verwaltung - Liste
 * 
 * Getrennte Ansichten für Kundengruppen vs Sammelposten:
 * - mode="customer-groups": Zeigt nur COLLECTIVE/RESIDENT
 * - mode="system-buckets": Zeigt nur SYSTEM_BUCKET (mit Create/Edit)
 * 
 * UX-Leitlinie: Keine Modals für Create/Edit, nur Liste → Detailseite
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Package, Plus, RefreshCw, ChevronRight, Info } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

export type CustomerGroupPageMode = 'customer-groups' | 'system-buckets';

interface CustomerGroup {
  id: string;
  code: string;
  name: string;
  type: string;
  systemCode: string | null;
  isActive: boolean;
  customerCount: number;
}

const sortAlpha = <T extends { name: string }>(items: T[]): T[] => {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }));
};

interface Props {
  mode: CustomerGroupPageMode;
}

export default function CustomerGroupsPage({ mode }: Props) {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<CustomerGroup[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Derived state based on mode
  const isSystemBucketMode = mode === 'system-buckets';
  const baseRoute = isSystemBucketMode ? '/admin/system-buckets' : '/admin/customer-groups';

  // Load groups
  const loadGroups = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/customer-groups', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setGroups(data);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Kundengruppen:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadGroups();
  }, []);

  // Filter by mode + active + alphabetisch sortieren
  const filtered = sortAlpha(
    groups.filter(g => {
      if (g.isActive === false) return false;
      if (isSystemBucketMode) {
        return g.type === 'SYSTEM_BUCKET';
      } else {
        return g.type !== 'SYSTEM_BUCKET'; // COLLECTIVE or RESIDENT
      }
    })
  );

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              {isSystemBucketMode ? <Package className="w-6 h-6" /> : <Users className="w-6 h-6" />}
              {isSystemBucketMode ? 'Sammelposten' : 'Kundengruppen'}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {filtered.length} {isSystemBucketMode ? 'Sammelposten' : 'Kundengruppen'}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadGroups}
              disabled={loading}
              className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              title="Aktualisieren"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => navigate(`${baseRoute}/new`)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {isSystemBucketMode ? 'Neuer Sammelposten' : 'Neue Kundengruppe'}
            </button>
          </div>
        </div>

        {/* Info-Box für Sammelposten */}
        {isSystemBucketMode && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
            <div className="text-sm text-orange-800">
              <p className="font-medium">Sammelposten sind Produktionskategorien</p>
              <p className="mt-1">Sammelposten (z.B. Weiß, Farbe, Bewohnerwäsche) dienen der Produktionssteuerung bei der WSA-Erfassung. 
              Kunden werden <strong>nicht</strong> fest zugeordnet – stattdessen werden bei der Erfassung die beteiligten Kunden angegeben.</p>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Laden...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {isSystemBucketMode ? 'Keine Sammelposten vorhanden' : 'Keine Kundengruppen vorhanden'}
              <button 
                onClick={() => navigate(`${baseRoute}/new`)}
                className="block mx-auto mt-4 text-blue-600 hover:underline"
              >
                {isSystemBucketMode ? 'Ersten Sammelposten anlegen' : 'Erste Kundengruppe anlegen'}
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                  {!isSystemBucketMode && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Kunden</th>
                  )}
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map(g => (
                  <tr 
                    key={g.id} 
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`${baseRoute}/${g.id}`)}
                  >
                    <td className="px-4 py-3 font-medium">
                      {g.name}
                    </td>
                    {!isSystemBucketMode && (
                      <td className="px-4 py-3 text-right text-slate-600">{g.customerCount}</td>
                    )}
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
